/**
 * Utility function to get the correct image URL based on the source
 * Handles:
 * - External URLs (http/https)
 * - CMS uploaded images (/uploads/)
 * - Local assets (/assets/)
 * 
 * @param {string} imagePath - The image path from database or hardcoded
 * @returns {string} - The resolved image URL
 */
export function getImageUrl(imagePath) {
  if (!imagePath) {
    return ''
  }

  const cleanedPath = String(imagePath).trim().replace(/^\/up\s+loads\//i, '/uploads/')

  // Use REACT_APP_CMS_BASE_URL for image base (images always need full URL)
  // Fall back to deriving from CMS_API_URL for backwards compatibility
  const CMS_BASE = process.env.REACT_APP_CMS_BASE_URL
  const CMS_API = process.env.REACT_APP_CMS_API_URL || 'https://jriit.beetletz.online'
  const baseUrl = CMS_BASE
    ? CMS_BASE.replace(/\/$/, '')
    : CMS_API.replace('/api/public', '').replace(/\/$/, '')

  // If it's an absolute URL whose path is a CMS upload, rewrite it to use our configured
  // CMS base URL. This is necessary because the CMS API may return absolute URLs pointing
  // to the production host even when running locally (localhost detected as internal and skipped).
  if (cleanedPath.startsWith('http://') || cleanedPath.startsWith('https://')) {
    try {
      const parsed = new URL(cleanedPath)
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${baseUrl}${parsed.pathname}${parsed.search}${parsed.hash}`
      }
    } catch {
      // Malformed URL — fall through and return as-is
    }
    return cleanedPath
  }

  // If it's from CMS uploads folder, prepend CMS domain
  // In production, this should be your CMS domain
  // For development, use localhost:3000 (CMS port)
  if (cleanedPath.startsWith('/uploads/') || cleanedPath.startsWith('uploads/')) {
    // Ensure path starts with /
    const normalizedPath = cleanedPath.startsWith('/') ? cleanedPath : `/${cleanedPath}`
    return `${baseUrl}${encodeURI(normalizedPath)}`
  }
  
  // If it's a path that starts with / but isn't /assets/, it might be a CMS path
  // Check if it looks like a file path (has extension)
  if (cleanedPath.startsWith('/') && !cleanedPath.startsWith('/assets/') && /\.(jpg|jpeg|png|gif|webp|svg|avif|ico)$/i.test(cleanedPath)) {
    return `${baseUrl}${encodeURI(cleanedPath)}`
  }

  // If it's a local asset path, try to require it
  // This handles the fallback case for hardcoded components
  if (cleanedPath.startsWith('/assets/')) {
    try {
      // For React, we need to use require with the actual path
      // This will work for images in src/assets/
      // For dynamic paths, return the path as-is and let the component handle it
      // Note: This requires the image to be imported or available at build time
      // For dynamic paths, we'll return the path as-is and let the component handle it
      return cleanedPath
    } catch (e) {
      // If require fails, return the path as-is
      // The component should handle fallback
      return cleanedPath
    }
  }

  // For any other path, return as-is
  return cleanedPath
}

/**
 * Get image URL with fallback
 * @param {string} imagePath - Primary image path
 * @param {string} fallbackPath - Fallback image path if primary fails
 * @returns {string} - The image URL to use
 */
export function getImageUrlWithFallback(imagePath, fallbackPath = '/assets/default-program.jpg') {
  const primaryUrl = getImageUrl(imagePath)
  return primaryUrl || getImageUrl(fallbackPath)
}

/** Inline SVG placeholder for broken images - no external request */
export const FALLBACK_IMAGE_SVG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3Ctext fill='%239ca3af' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Image%3C/text%3E%3C/svg%3E";

